import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  createCreditEconomyHelpers,
  normalizeCreditEconomyConfig,
} from '../utils/creditEconomyConfig';

const CreditEconomyContext = createContext(null);

export function CreditEconomyProvider({ children }) {
  const [raw, setRaw] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'system', 'creditEconomy');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setRaw(snap.exists() ? snap.data() : null);
        setLoaded(true);
      },
      (err) => {
        console.warn('[CreditEconomy]', err);
        setLoaded(true);
      },
    );
    return () => unsub?.();
  }, []);

  const config = useMemo(() => normalizeCreditEconomyConfig(raw), [raw]);
  const helpers = useMemo(() => createCreditEconomyHelpers(config), [config]);

  const value = useMemo(
    () => ({
      loaded,
      config,
      ...helpers,
    }),
    [loaded, config, helpers],
  );

  return (
    <CreditEconomyContext.Provider value={value}>
      {children}
    </CreditEconomyContext.Provider>
  );
}

export function useCreditEconomy() {
  const ctx = useContext(CreditEconomyContext);
  if (!ctx) {
    throw new Error('useCreditEconomy must be used within CreditEconomyProvider');
  }
  return ctx;
}

/** Safe outside provider — uses defaults only. */
export function useCreditEconomyOptional() {
  const ctx = useContext(CreditEconomyContext);
  if (ctx) return ctx;
  return createCreditEconomyHelpers(null);
}
